import { useState, useMemo } from 'react';
export type SortDirection = 'asc' | 'desc';
export interface SortConfig<T> {
  key: string;
  direction: SortDirection;
}
export type AccessorFn<T> = (item: T) => any;
export function useSortableData<T>(items: T[], accessors: Record<string, AccessorFn<T>> = {}) {
  const [sortConfig, setSortConfig] = useState<SortConfig<T> | null>(null);
  const sortedItems = useMemo(() => {
    if (!sortConfig) return items;
    const sortableItems = [...items];
    sortableItems.sort((a, b) => {
      const accessor = accessors[sortConfig.key];
      let aValue = accessor ? accessor(a) : (a as any)[sortConfig.key];
      let bValue = accessor ? accessor(b) : (b as any)[sortConfig.key];

      // Handle null/undefined
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Handle dates
      if (aValue instanceof Date) aValue = aValue.getTime();
      if (bValue instanceof Date) bValue = bValue.getTime();
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }

      // String comparison fallback
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return 0;
    });
    return sortableItems;
  }, [items, sortConfig, accessors]);
  const requestSort = (key: string) => {
    let direction: SortDirection = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({
      key,
      direction
    });
  };
  return {
    items: sortedItems,
    requestSort,
    sortConfig
  };
}